/**
 * Brand color SSOT: the raw hexes the FEDERA brand identity is defined by, each spelled ONCE.
 *
 * WHY `const/` AND NOT THE BRAND TOKEN TREE. `utils/styles/interaction-tokens.ts` renders the focus ring in the brand
 * primary and sits on the utils floor, which admits only `@federa/const`, `@federa/types` and `zod` -- so
 * `brand/federa/tokens/colors.ts` is structurally unreachable from it and the hex was HAND-MIRRORED, with a sibling
 * test pinning the copy against drift. `const/` is the one home BOTH layers may import, so the mirror is deleted and
 * the pin is on the IMPORT relationship rather than on a re-typed literal.
 *
 * NOT a member of `const/design-token-scales.ts`: that leaf's own docstring excludes brand-specific values (colors,
 * font families, gradients) because it is the brand-AGNOSTIC scale SSOT. This leaf is its brand-specific counterpart,
 * and the two must not merge for that reason.
 *
 * Every consumer reads a MEMBER: `brand/federa/tokens/colors.ts` (the primary ramp's 600 rung, `brand.primaryLight` and
 * `brand.navy`), `utils/styles/semantic-colors.ts` (`success`), `utils/styles/interaction-tokens.ts` (the focus ring),
 * `components/title-report/polished-report-tokens.ts` (the report letterhead ink) and
 * `spa/chats/frontend/src/chats-viewer-tokens.ts` (the `var()` fallbacks its var()-unreachable pre-gate shell needs).
 * A re-typed literal at any of them is the waste `DS-BRAND-HEX-LITERAL-SSOT-001` forbids.
 *
 * MEMBERSHIP IS "A BRAND HUE NO RAMP CARRIES", not "a hue the logo is drawn in", and `primaryLight` is what settled it
 * (2026-09-03). It is published as `--color-brand-primary-light` and read by a dozen surfaces, it is carried by no
 * `COLOR_RAMPS` rung (`primary[100]` is `#E8F5EE`, a different colour), and it lived as a literal in
 * `brand/federa/tokens/colors.ts` -- structurally unreachable from `spa/`, which therefore TRANSCRIBED it into a
 * `var()` fallback in lower case. Both hex-SSOT rules were blind to that copy BY CONSTRUCTION: it was neither a ramp
 * rung nor a member here, so the one class those two rules exist to catch had a hue nothing guarded. Admitting it is
 * the cure `DS-BRAND-HEX-LITERAL-SSOT-001`'s own `fix` prescribes -- "a new brand colour joins BRAND_PALETTE itself,
 * and this rule then guards it with no edit here" -- and it costs no new queue: the hex occurs exactly twice in the
 * tree (both bound in the same change) and its decimal spelling `232,245,233` not at all.
 */

export const BRAND_PALETTE = {
	/** FEDERA forest green. ONE hex, three roles: the primary ramp's 600 rung, the `success` semantic, and the focus ring. */
	primary: '#0B6836',
	/** The pale brand wash -- `colors.brand.primaryLight`, the fill behind brand-tinted callouts, badges and selected
	 *  cells. Deliberately NOT `COLOR_RAMPS.primary[100]`: that rung is `#E8F5EE` and these are two different colours
	 *  that a reader comparing them by eye would fold. */
	primaryLight: '#E8F5E9',
	/** Deep document/title navy -- the "View Title Report" CTA background and the polished title-report letterhead ink. */
	navy: '#0a3d62',
	/**
	 * The muted hairline -- `colors.border.muted`, the quiet divider a surface uses where `border.default` reads too
	 * heavy.
	 *
	 * ADMITTED 2026-09-03 for the reason `primaryLight` was, by the same mechanism and from the same transcribing leaf:
	 * it was a LITERAL in `brand/federa/tokens/colors.ts`, structurally unreachable from `spa/`, so
	 * `spa/chats/frontend/src/chats-viewer-tokens.ts` re-typed it LOWER-CASED into its var()-unreachable pre-gate shell.
	 * Both hex-SSOT rules were blind to that copy BY CONSTRUCTION -- they derive from `COLOR_RAMPS` and this leaf, and
	 * the hue was in neither -- so the one class those rules exist to catch had a third hue nothing guarded.
	 *
	 * Deliberately NOT `COLOR_RAMPS.neutral[200]`: that rung is `#E0E3E7`. The two sit two hex digits apart in the last
	 * pair and read identically on a screen, which is exactly the fold a reader comparing them by eye would make -- and
	 * `colors.border.default` ALREADY binds that rung one line above `muted`, so the file itself distinguishes them.
	 */
	borderMuted: '#E5E7EB',
} as const;
